import React, { useState, useEffect } from 'react';
import { WifiOff, Wifi, CloudOff, Cloud as CloudSync } from 'lucide-react';
import { hasPendingSync, getPendingSyncCount, processSyncQueue } from '../lib/offlineStorage';

export function OfflineIndicator() {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [pendingSync, setPendingSync] = useState<boolean>(false);
  const [pendingSyncCount, setPendingSyncCount] = useState<number>(0);
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [syncing, setSyncing] = useState<boolean>(false);

  // Check online status and pending syncs
  useEffect(() => {
    const checkStatus = async () => {
      const hasPending = await hasPendingSync();
      setPendingSync(hasPending);
      
      if (hasPending) {
        const count = await getPendingSyncCount();
        setPendingSyncCount(count);
      }
    };
    
    // Check immediately
    checkStatus();
    
    // Set up event listeners
    const handleOnline = () => {
      setIsOnline(true);
      checkStatus();
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      checkStatus();
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Set up interval to check pending syncs
    const interval = setInterval(checkStatus, 10000);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, []);

  // Handle manual sync
  const handleSync = async () => {
    if (!isOnline || syncing) return;
    
    try {
      setSyncing(true);
      await processSyncQueue();
      
      // Check if we still have pending syncs
      const hasPending = await hasPendingSync();
      setPendingSync(hasPending);
      
      if (hasPending) {
        const count = await getPendingSyncCount();
        setPendingSyncCount(count);
      }
    } catch (error) {
      console.error('Error syncing:', error);
    } finally {
      setSyncing(false);
    }
  };

  // Don't show anything if online and no pending syncs
  if (isOnline && !pendingSync) {
    return null;
  }

  return (
    <div 
      className={`fixed bottom-4 right-4 z-50 ${showDetails ? 'w-64' : 'w-auto'} transition-all duration-200`}
      onMouseEnter={() => setShowDetails(true)}
      onMouseLeave={() => setShowDetails(false)}
    >
      <div 
        className={`
          rounded-lg shadow-lg p-3 
          ${isOnline 
            ? pendingSync 
              ? 'bg-gold text-white' 
              : 'bg-emerald text-white'
            : 'bg-crimson text-white'
          }
          flex items-center
          ${showDetails ? 'w-full' : 'w-auto'}
          cursor-pointer
          transition-all duration-200
        `}
        onClick={() => setShowDetails(!showDetails)}
      >
        {!isOnline && (
          <WifiOff className="h-5 w-5 flex-shrink-0" />
        )}
        
        {isOnline && pendingSync && (
          <CloudSync className={`h-5 w-5 flex-shrink-0 ${syncing ? 'animate-spin' : ''}`} />
        )}
        
        {showDetails && (
          <div className="ml-3 flex-1">
            {!isOnline && (
              <div>
                <p className="font-medium">You're offline</p>
                <p className="text-xs">Changes will be saved locally</p>
              </div>
            )}
            
            {isOnline && pendingSync && (
              <div>
                <p className="font-medium">Syncing pending changes</p>
                <p className="text-xs">{pendingSyncCount} {pendingSyncCount === 1 ? 'item' : 'items'} waiting to sync</p>
              </div>
            )}
            
            {isOnline && pendingSync && (
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  handleSync();
                }}
                className="mt-2 px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-xs font-medium"
                disabled={syncing}
              >
                {syncing ? 'Syncing...' : 'Sync Now'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}